import { Card } from "../Card";
import { DesktopOnly, MobileOnly } from "../ResponsiveTableList";
import { Skeleton } from "../Skeleton";
import { Table, Tbody, Td, Th, Thead, Tr } from "../Table";
import type { Scale } from "../tokens";
import { anchoBarra, normalizarColumnas, type ColumnaNormalizada, type ColumnaSkeleton } from "./columnas";
import { ListaTarjetasSkeleton } from "./ListaTarjetasSkeleton";
import { RegionCargando } from "./RegionCargando";

export interface TablaSkeletonProps {
  /** Columnas REALES de la tabla (mismos pesos/alineación) o un número de columnas iguales. */
  columnas: ReadonlyArray<ColumnaSkeleton> | number;
  filas?: number;
  /** Columna de selección (checkbox) al inicio. */
  conCheckbox?: boolean;
  /** Solo el `<Tbody>` · para usarlo dentro de una `<Table>` existente con su `<Thead>` real. */
  dentroDeTabla?: boolean;
  /** Segunda fila del encabezado con un input de filtro por columna (tablas con filtros por columna). */
  conFiltrosPorColumna?: boolean;
  /** Barra de herramientas (botón de filtros + paginación) sobre la tabla. */
  conToolbar?: boolean;
  /** Agrega la variante móvil (`ListaTarjetasSkeleton`) · la tabla queda solo en escritorio. */
  movil?: boolean;
  /** min-width de la tabla real (px) · para que las columnas midan lo mismo. */
  minWidth?: number;
  verticalSpacing?: Scale;
  etiqueta?: string;
}

const W_CHECK = 44;

/**
 * Filas de carga con las columnas reales de una tabla (evita el salto de layout
 * al llegar los datos). Tres usos: tabla completa en su `Card` (encabezado
 * incluido), solo el `<Tbody>` dentro de una tabla existente (`dentroDeTabla`) y
 * con variante de tarjetas en móvil (`movil`).
 */
export function TablaSkeleton({
  columnas,
  filas = 15,
  conCheckbox = false,
  dentroDeTabla = false,
  conFiltrosPorColumna = false,
  conToolbar = false,
  movil = false,
  minWidth,
  verticalSpacing = "md",
  etiqueta,
}: TablaSkeletonProps) {
  const cols = normalizarColumnas(columnas);
  if (dentroDeTabla) return <CuerpoSkeleton cols={cols} filas={filas} conCheckbox={conCheckbox} anunciar etiqueta={etiqueta} />;

  const tabla = (
    <Card padding="xs" className="overflow-hidden p-0">
      <div className="overflow-x-hidden">
        <Table layout="fixed" minWidth={minWidth} verticalSpacing={verticalSpacing} horizontalSpacing="sm">
          <Thead className="bg-ink-100 dark:bg-ink-800">
            <Tr>
              {conCheckbox && (
                <Th style={{ width: W_CHECK }}>
                  <Skeleton height={16} width={16} className="rounded-sm" />
                </Th>
              )}
              {cols.map((c, i) => (
                <Th key={i} align={c.align} style={{ width: c.pct }}>
                  <Barra align={c.align} width="55%" height={10} />
                </Th>
              ))}
            </Tr>
            {conFiltrosPorColumna && (
              <Tr>
                {conCheckbox && <Th />}
                {cols.map((c, i) => (
                  <Th key={i} className="!p-1">
                    <Skeleton height={28} />
                  </Th>
                ))}
              </Tr>
            )}
          </Thead>
          <CuerpoSkeleton cols={cols} filas={filas} conCheckbox={conCheckbox} />
        </Table>
      </div>
    </Card>
  );

  return (
    <RegionCargando etiqueta={etiqueta} className="flex flex-col gap-3">
      {conToolbar && <ToolbarSkeleton />}
      {movil ? (
        <>
          <DesktopOnly>{tabla}</DesktopOnly>
          <MobileOnly>
            <ListaTarjetasSkeleton tarjetas={Math.min(filas, 5)} anunciar={false} />
          </MobileOnly>
        </>
      ) : (
        tabla
      )}
    </RegionCargando>
  );
}

/** Toolbar de tabla (botón «Filtros» a la izquierda, paginación a la derecha). */
export function ToolbarSkeleton() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-2" aria-hidden="true">
      <Skeleton height={32} width={96} />
      <Skeleton height={28} width={220} />
    </div>
  );
}

function CuerpoSkeleton({
  cols,
  filas,
  conCheckbox,
  anunciar = false,
  etiqueta = "Cargando…",
}: {
  cols: ColumnaNormalizada[];
  filas: number;
  conCheckbox: boolean;
  anunciar?: boolean;
  etiqueta?: string;
}) {
  return (
    <Tbody aria-busy="true">
      {Array.from({ length: filas }, (_, f) => (
        <Tr key={f}>
          {conCheckbox && (
            <Td>
              <Skeleton height={16} width={16} className="rounded-sm" />
            </Td>
          )}
          {cols.map((c, i) => (
            <Td key={i} align={c.align}>
              {/* El anuncio para lectores de pantalla vive en la primera celda (un
                  <span> no puede ir suelto dentro de <tbody>). */}
              {anunciar && f === 0 && i === 0 && (
                <span role="status" className="sr-only">
                  {etiqueta}
                </span>
              )}
              <Barra align={c.align} width={anchoBarra(f, i)} height={14} />
              {c.lineas === 2 && <Barra align={c.align} width="45%" height={10} className="mt-1.5" />}
            </Td>
          ))}
        </Tr>
      ))}
    </Tbody>
  );
}

function Barra({
  align,
  width,
  height,
  className,
}: {
  align: ColumnaNormalizada["align"];
  width: string;
  height: number;
  className?: string;
}) {
  const alinear = align === "right" ? "ml-auto" : align === "center" ? "mx-auto" : undefined;
  return <Skeleton height={height} width={width} className={[alinear, className].filter(Boolean).join(" ")} />;
}
